import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Shield, AlertCircle } from 'lucide-react';

const CallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const processedRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double processing
      if (processedRef.current) return;
      processedRef.current = true;

      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const storedState = sessionStorage.getItem('oidc_state');

      if (!code) {
        setError('Hiányzó authorization code');
        setIsProcessing(false);
        return;
      }

      // State validation - allow if state matches OR if no stored state (direct login)
      if (storedState && state !== storedState) {
        console.warn('State mismatch - stored:', storedState, 'received:', state);
        // Don't show error immediately, try to login anyway if state exists
        if (!state) {
          setError('Érvénytelen state token');
          setIsProcessing(false);
          return;
        }
      }

      // Clear stored state
      sessionStorage.removeItem('oidc_state');

      try {
        const result = await login(code, state);
        if (result.success) {
          navigate('/', { replace: true });
        } else {
          setError(result.error || 'Bejelentkezési hiba');
          setIsProcessing(false);
        }
      } catch (err) {
        console.error('Login error:', err);
        setError('Bejelentkezési hiba történt');
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [searchParams, login, navigate]);

  if (error && !isProcessing) {
    return (
      <div className="min-h-screen animated-gradient flex items-center justify-center">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-white/20 max-w-md w-full mx-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-full mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Hiba történt</h2>
          <p className="text-white/70 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-3 bg-white text-primary-700 font-semibold rounded-xl hover:bg-white/90 transition-all"
          >
            Vissza a bejelentkezéshez
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen animated-gradient flex items-center justify-center">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-white/20 max-w-md w-full mx-4 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4 animate-pulse">
          <Shield className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-xl font-semibold text-white mb-2">Bejelentkezés folyamatban</h2>
        <p className="text-white/70">Kérjük, várjon...</p>
        <div className="mt-6 flex justify-center">
          <div className="spinner border-white/30 border-t-white"></div>
        </div>
      </div>
    </div>
  );
};

export default CallbackPage;
